import { Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import './utils/toastPatch';
import { changeFeatureFlags } from 'actions/featureFlagActions';
import { updateCurrentAOR } from 'actions/locationAction';
import {
  createOrGetUser,
  fetchGroups,
  updateCurrentlySelected,
} from './actions/profileActions';
import {
  updateCurrentIncident,
  updateCurrentlySelectedGroup,
} from './actions/diceActions';
import { validateRBACPermissionForAction } from './actions/validationActions';
import { clearNotificationStatus } from 'actions/notificationActions';
import { DgptStreamProvider } from './providers/DgptStreamProvider';
import { AppEventsProvider } from './providers/AppEventsProvider';
import { createBrowserRouter, useLocation, useNavigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { trackUserLogin } from 'actions/trackingActions';
import Loader from 'components/Loader/Loader';
import ErrorBoundary from 'components/Error/ErrorBoundary';
import { updateTeamsTenants } from 'actions/teamsActions';
import PratusRoutes from './routes/PratusRoutes';
import TeamsTabRemove from 'components/TeamsIntegration/TeamsTabRemove';
import RouteError from './components/Error/RouteError';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useIsDev } from './hooks/useIsDev';
import { useEnvironmentVariables } from './utils/useEnvironmentVariables';
import { useAors } from 'hooks/useAors';
import ICSContextProvider from './components/IAP/planningView/ICSContext';
import { useNotificationStream } from 'hooks/useNotificationStream';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import { POSTHOG_HOST } from './environment';
import { useAxiosInterceptor } from './hooks/useAxiosInterceptor';
import Eruda from './components/Eruda/Eruda';
import { ThemeProvider } from '@/components/theme-provider';

import './styles/tailwind.css';

let notifyPostHogReady = null;
export const setPostHogReadyCallback = (callback) => {
  notifyPostHogReady = callback;
};

function App() {
  // location
  const location = useLocation();
  const navigate = useNavigate();
  const reduxDispatch = useDispatch();

  const reduxGroups = useSelector((state) => {
    return state.app.groups;
  });

  const areGroupsStable = useSelector((state) => {
    return state.app.areGroupsLoaded;
  });

  const groups = useSelector((state) => {
    return state.app.groups;
  });

  const isDev = useIsDev();

  const userAgent = useRef(navigator.userAgent);
  // Latches once a currently-selected group has been established for this session,
  // so a transient invalid selection can't silently reset the user to groups[0].
  const hasBootstrappedGroupRef = useRef(false);

  const auth = useSelector((state) => {
    return state.auth;
  });

  const user = useSelector((state) => {
    return state.app.user;
  });

  const reduxIncidents = useSelector((state) => {
    return state.app.incidents;
  });

  const reduxCurrentIncident = useSelector((state) => {
    return state.app.currentIncident;
  });

  const areIncidentsLoaded = useSelector((state) => {
    return state.app.areIncidentsLoaded;
  });

  const { userName } = useSelector((state) => {
    return state.auth.account;
  });

  const reduxCurrentlySelectedGroup = useSelector((state) => {
    return state.app.currentlySelectedGroup;
  });

  const reduxValidateRBACPermissionForActionResult = useSelector((state) => {
    return state.app.validateRBACPermissionForActionResult;
  });

  const isValidateRBACPermissionForActionLoaded = useSelector((state) => {
    return state.app.isValidateRBACPermissionForActionLoaded;
  });

  const reduxFeatureFlags = useSelector((state) => {
    return state.app.featureFlags;
  });

  const reduxCurrentAOR = useSelector((state) => {
    return state.app.currentAOR;
  });

  const [
    hasNewUserNavigatedAwayFromProfile,
    setHasNewUserNavigatedAwayFromProfile,
  ] = useState(false);

  const { data: aors = [] } = useAors();
  useNotificationStream();
  useAxiosInterceptor();

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Tab') {
        document.body.classList.add('user-is-tabbing');
      }
    }

    function handleMouseDown() {
      document.body.classList.remove('user-is-tabbing');
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  useEffect(() => {
    if (
      userAgent.current.includes('Safari') &&
      !!auth &&
      !auth.teamsInitialized
    ) {
      const split = userAgent.current.split(' Safari');
      const innerSplit = split[0].split('/');
      const version = innerSplit[innerSplit.length - 1];
      if (parseFloat(version) < 15.4) {
        // eslint-disable-next-line no-alert
        window.alert(
          'Your browser is out of date, please update or use an alternate browser.'
        );
      }
    }
  }, [userAgent, auth]);

  useEffect(() => {
    if (auth.teamsInitialized) {
      const token = auth.accessToken;
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tenantId = payload.tid;
      if (tenantId) {
        reduxDispatch(updateTeamsTenants(tenantId));
      }
    }
  }, [auth, reduxDispatch]);

  useEffect(() => {
    reduxDispatch(trackUserLogin());

    reduxDispatch(
      createOrGetUser({
        userName,
      })
    );
  }, [userName, reduxDispatch]);

  const { isFetched: isEnvironmentVariableLoaded, data: envData } =
    useEnvironmentVariables(!!userName);

  // Initialize PostHog when environment variables are loaded
  useEffect(() => {
    if (envData?.POSTHOG_API_KEY && POSTHOG_HOST && !posthog.__loaded) {
      try {
        posthog.init(envData.POSTHOG_API_KEY, {
          api_host: POSTHOG_HOST,
          person_profiles: 'always',
          loaded: () => {
            console.log('PostHog initialized successfully');
            notifyPostHogReady?.();
          },
        });
      } catch (error) {
        console.warn('Failed to initialize PostHog:', error);
      }
    }
  }, [envData]);

  useEffect(() => {
    const defaultFeatureFlagsFromEnv = envData?.DEFAULT_FEATURE_FLAGS;

    if (defaultFeatureFlagsFromEnv) {
      const s = new Set([
        ...reduxFeatureFlags,
        ...defaultFeatureFlagsFromEnv.trim().split(','),
      ]);

      reduxDispatch(changeFeatureFlags(Array.from(s)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnvironmentVariableLoaded]);

  useEffect(() => {
    const { user_guid } = user || {};
    if (user && user_guid) {
      // reduxDispatch(fetchIncidents());
      if (
        areGroupsStable &&
        !groups &&
        JSON.stringify(reduxCurrentlySelectedGroup) !== '{}'
      ) {
        reduxDispatch(fetchGroups());
      }
    }
  }, [
    user,
    areGroupsStable,
    groups,
    reduxCurrentlySelectedGroup,
    reduxDispatch,
  ]);

  useEffect(() => {
    if (
      !reduxGroups ||
      !areGroupsStable ||
      !isValidateRBACPermissionForActionLoaded ||
      !reduxGroups.length
    ) {
      return;
    }

    const currentSelectionValid =
      !!reduxCurrentlySelectedGroup &&
      !!reduxCurrentlySelectedGroup.group_guid &&
      reduxGroups.some(
        (g) => g.group_guid === reduxCurrentlySelectedGroup.group_guid
      );

    if (currentSelectionValid) {
      hasBootstrappedGroupRef.current = true;
      return;
    }

    // Present-but-not-in-groups means the org went away mid-session (user removed,
    // org deleted, etc.). Recover even if we've already bootstrapped, otherwise the
    // app stays stuck referencing a group the user can no longer see.
    const selectionPresentButInvalid =
      !!reduxCurrentlySelectedGroup && !!reduxCurrentlySelectedGroup.group_guid;

    if (hasBootstrappedGroupRef.current && !selectionPresentButInvalid) {
      return;
    }

    // Prefer the server-persisted selection when it exists in the loaded groups;
    // only fall back to the first group on a genuine first-login / stale-selection bootstrap.
    const persistedGuid = user?.currently_selected?.group_guid;
    const persistedGroup = persistedGuid
      ? reduxGroups.find((g) => g.group_guid === persistedGuid)
      : undefined;
    const nextGroup = persistedGroup ?? reduxGroups[0];

    hasBootstrappedGroupRef.current = true;
    reduxDispatch(updateCurrentlySelectedGroup(nextGroup));
  }, [
    reduxCurrentlySelectedGroup,
    reduxGroups,
    areGroupsStable,
    isValidateRBACPermissionForActionLoaded,
    reduxDispatch,
    user,
  ]);

  useEffect(() => {
    if (
      !!reduxCurrentlySelectedGroup &&
      !!reduxCurrentlySelectedGroup.group_guid &&
      !!reduxGroups &&
      !!reduxGroups.find(
        (g) => g.group_guid === reduxCurrentlySelectedGroup.group_guid
      )
    ) {
      reduxDispatch(
        validateRBACPermissionForAction(reduxCurrentlySelectedGroup.group_guid)
      );

      if (
        reduxCurrentIncident?.group_guid ===
        reduxCurrentlySelectedGroup?.group_guid
      ) {
        // do nothing
      } else if (reduxIncidents) {
        const newCurrentGroupIncident = reduxIncidents.filter(
          (i) => i.group_guid === reduxCurrentlySelectedGroup.group_guid
        )[0];
        reduxDispatch(updateCurrentIncident(newCurrentGroupIncident));
      }
    }
  }, [
    reduxCurrentlySelectedGroup,
    reduxIncidents,
    // isValidateRBACPermissionForActionLoaded,
    reduxCurrentIncident,
    reduxDispatch,
    reduxGroups,
  ]);

  useEffect(() => {
    if (!!user && !!user.currently_selected && !!aors) {
      if (aors.length) {
        const foundAOR = aors.find(
          (aor) => aor.id === user.currently_selected.aor_id
        );
        if (!foundAOR && !!aors.length) {
          reduxDispatch(updateCurrentAOR(aors[0]));
        } else if (!reduxCurrentAOR || reduxCurrentAOR.id !== foundAOR.id) {
          reduxDispatch(updateCurrentAOR(foundAOR));
        }
      }
    }
  }, [user, aors, reduxCurrentAOR, reduxDispatch]);

  const isUpdateCurrentlySelectedLoaded = useSelector((state) => {
    return state.app.isUpdateCurrentlySelectedLoaded;
  });

  const isSavedRouteValid = (route) => {
    const iapMatch = route?.match(/^\/iap\/([^/]+)/);
    if (iapMatch) {
      const incidentId = iapMatch[1];
      return reduxIncidents?.some(
        (inc) => inc && String(inc.id) === String(incidentId)
      );
    }
    return true;
  };

  useEffect(() => {
    if (!!user && !!user.currently_selected && !!location) {
      if (
        !!isUpdateCurrentlySelectedLoaded &&
        (!user.currently_selected.route ||
          (!!user.currently_selected.route &&
            user.currently_selected.route !== location.pathname))
      ) {
        reduxDispatch(updateCurrentlySelected({ route: location.pathname }));
      }
    }
    if (!location.pathname.includes('notifications')) {
      reduxDispatch(clearNotificationStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (
      !!user &&
      user.profile_settings.is_new_user === 'true' &&
      !hasNewUserNavigatedAwayFromProfile
    ) {
      setHasNewUserNavigatedAwayFromProfile(true);
    } else if (
      sessionStorage.initialPath?.includes('/iap') ||
      sessionStorage.initialPath?.includes('/incident_configuration') ||
      sessionStorage.initialPath?.includes('/teamsconfig') ||
      sessionStorage.initialPath?.includes('/teamsremove')
    ) {
      navigate(sessionStorage.initialPath);
      sessionStorage.initialPath = undefined;
    } else if (
      !!user &&
      location.pathname === '/' &&
      !!user.currently_selected &&
      !!user.currently_selected.route &&
      areIncidentsLoaded
    ) {
      const savedRoute = user.currently_selected.route;
      const targetRoute = isSavedRouteValid(savedRoute) ? savedRoute : '/hub';
      navigate(targetRoute);
    } else if (
      user?.external_user &&
      (location.pathname === '/' ||
        location.pathname === '/hub' ||
        location.pathname === '/external_user_no_incidents')
    ) {
      if (!reduxIncidents || reduxIncidents.length === 0) {
        navigate('/external_user_no_incidents');
      } else if (areIncidentsLoaded) {
        navigate(`/iap/${reduxIncidents[0].id}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user,
    reduxIncidents,
    areIncidentsLoaded,
    navigate,
    hasNewUserNavigatedAwayFromProfile,
  ]);

  // Keep sessionStorage in sync with Redux auth.
  // Redux auth is the source of truth; sessionStorage mirrors it for interceptor usage.
  useEffect(() => {
    if (auth?.accessToken) {
      sessionStorage['accessToken'] = auth.accessToken;
      sessionStorage['accessTokenExpiresOn'] = auth.expiresOn;
      sessionStorage['isUsingTeams'] = auth.teamsInitialized ? 'true' : 'false';
    }
  }, [auth.accessToken, auth.expiresOn, auth.teamsInitialized]);

  // Teams enables its Remove button five seconds in and invokes the handler whether or not we are ready, so this page
  // skips the bootstrap gate below; it needs only the URL parameter, the Teams context and the cached access token.
  if (location.pathname === '/teamsremove') {
    return <TeamsTabRemove />;
  }

  if (
    typeof user === 'undefined' ||
    !reduxIncidents ||
    !reduxGroups ||
    (!isValidateRBACPermissionForActionLoaded &&
      !reduxValidateRBACPermissionForActionResult)
  ) {
    return <Loader showLoading={true} />;
  }

  return (
    <Suspense>
      <ErrorBoundary location={location}>
        {isEnvironmentVariableLoaded ? <PratusRoutes isDev={isDev} /> : null}
      </ErrorBoundary>
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    path: '*',
    element: <App />,
    errorElement: <RouteError />,
  },
]);

export const queryClient = new QueryClient();

// Conditionally wrap with PostHogProvider only if PostHog is initialized
const ConditionalPostHogProvider = ({ children }) => {
  const [isPostHogLoaded, setIsPostHogLoaded] = useState(posthog.__loaded);

  useEffect(() => {
    if (posthog.__loaded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsPostHogLoaded(true);
      return;
    }
    setPostHogReadyCallback(() => setIsPostHogLoaded(true));
  }, []);

  if (isPostHogLoaded) {
    return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
  }
  return children;
};

// We wrap App with the router here so we can use React Router tools like history/location at the top level component
const AppWrap = () => (
  <ErrorBoundary>
    <ConditionalPostHogProvider>
      <QueryClientProvider client={queryClient}>
        <DgptStreamProvider>
          <AppEventsProvider>
            <ICSContextProvider>
              <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
                <RouterProvider router={router} />
                <Eruda />
                {/*<ReactQueryDevtools /> /!** NOTE: excluded in production builds. TODO: hide this more. *!/*/}
              </ThemeProvider>
            </ICSContextProvider>
          </AppEventsProvider>
        </DgptStreamProvider>
      </QueryClientProvider>
    </ConditionalPostHogProvider>
  </ErrorBoundary>
);
AppWrap.displayName = 'AppWrap';
export default AppWrap;
